import React, { useEffect, useState } from 'react';
import { Select, Typography } from 'antd';
import { getAllGeiInventoryTableAPI, getGeiInventoryVersionsAPI } from 'src/services/restcall';
import { GeiInventoryListItem, GeiInventoryVersion, CURRENT_VERSION_VALUE } from 'src/geiVersion/types';
import {
    CONSOLIDATED_RESULTS_LABEL,
    ResultsVersionGroups,
    buildResultsVersionGroups,
    defaultResultsSelection
} from 'src/geiVersion/display';

const { Text } = Typography;

export interface ResultsVersionSelection {
    versionId?: string;
    label: string;
    state?: string;
    consolidated: boolean;
}

interface ResultsVersionSelectProps {
    organizationId: string;
    year: string;
    value: string;
    onChange: (selection: ResultsVersionSelection) => void;
}

const ResultsVersionSelect: React.FC<ResultsVersionSelectProps> = (props) => {
    const [catalog, setCatalog] = useState<ResultsVersionGroups>({ multipleInventories: false, groups: [] });
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            if (!props.organizationId || !props.year) {
                setCatalog({ multipleInventories: false, groups: [] });
                return;
            }
            setLoading(true);
            const inventoryResponse = await getAllGeiInventoryTableAPI(1, 500, 'NULL', 'NULL');
            const table = inventoryResponse.StatusCode === 200
                ? (inventoryResponse.Response?.geiInventoryTable || []) as GeiInventoryListItem[]
                : [];
            const yearNumber = Number(props.year);
            const matched = table.filter((item) =>
                String(item.msIdentity_organization_id || '') === props.organizationId
                && Number(item.year) === yearNumber
            );
            const versionsByInventory: Record<string, GeiInventoryVersion[]> = {};
            await Promise.all(matched.map(async (inventory) => {
                const versionResponse = await getGeiInventoryVersionsAPI(inventory.id);
                versionsByInventory[inventory.id] = versionResponse.StatusCode === 200 && Array.isArray(versionResponse.Response)
                    ? versionResponse.Response as GeiInventoryVersion[]
                    : [];
            }));
            if (cancelled) return;
            const nextCatalog = buildResultsVersionGroups(matched, versionsByInventory);
            setCatalog(nextCatalog);
            setLoading(false);
            props.onChange(defaultResultsSelection(nextCatalog));
        };
        load();
        return () => { cancelled = true; };
    }, [props.organizationId, props.year]);

    const versionOptions = (versions: ResultsVersionGroups['groups'][number]['versions']) =>
        versions.map((version) => ({
            label: version.label,
            value: version.versionId
        }));

    const options = catalog.multipleInventories || catalog.groups.length !== 1
        ? [
            { label: CONSOLIDATED_RESULTS_LABEL, value: CURRENT_VERSION_VALUE },
            ...catalog.groups.map((group) => ({
                label: group.inventoryLabel,
                options: versionOptions(group.versions)
            }))
        ]
        : versionOptions(catalog.groups[0].versions);

    return (
        <div>
            Versión
            <Select
                style={{ width: '100%' }}
                loading={loading}
                value={props.value || undefined}
                options={options}
                placeholder="Versión"
                onChange={(selected: string) => {
                    if (selected === CURRENT_VERSION_VALUE) {
                        props.onChange({
                            versionId: undefined,
                            label: CONSOLIDATED_RESULTS_LABEL,
                            consolidated: true
                        });
                        return;
                    }
                    const match = catalog.groups
                        .flatMap((group) => group.versions)
                        .find((version) => version.versionId === selected);
                    props.onChange({
                        versionId: selected,
                        label: match ? match.label : 'Versión',
                        state: match?.state,
                        consolidated: false
                    });
                }}
            />
            {catalog.multipleInventories ? (
                <Text type="secondary">
                    Seleccionar una versión específica muestra ese inventario. &apos;Actual (consolidado)&apos; consolida las versiones vigentes del año.
                </Text>
            ) : null}
        </div>
    );
};

export default ResultsVersionSelect;
